// ============================================================================
// Imports
// ============================================================================
import { useState, useMemo, useEffect, useRef } from "react";

// External libraries
import { Text, Tree, Button } from "@uhg-harmony/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@uhg-harmony/icons-react";

// Internal components
import { textDefaults } from "../ui/Text";
import { LibraryConfirmationModal } from "../modals/LibraryConfirmation";

// Types
import type { GetTemplateItem } from "../../redux/templates/templatesTypes";

// Redux
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import {
  selectTemplatesLoading,
  transformFoldersToTreeData,
  selectDataByCategory,
  selectAllTemplateFolders,
  selectActiveFolderNames,
} from "../../redux/templates/templatesTreeSelectors";
import {
  setFolderNames,
  updateMetadata,
  selectAccountId,
  selectClientId,
} from "../../redux/metadata/metadataSlice";
import { selectUserRole } from "../../redux/user";

// Styles
import "./EmailTemplate.css";

// ============================================================================
// Types
// ============================================================================
type TemplateWorkspaceProps = {
  onEmailTemplateLibraryClick: () => void;
  isLeftCollapsed: boolean;
  onToggleLeft: () => void;
  category?: string;
  isTemplateDraft?: boolean;
  isSaveAsDraftDisabled?: boolean;
  onSaveAsDraft?: () => void;
  onTemplateSelected?: (template: GetTemplateItem, folderName: string) => void;
  onTemplateLoading?: () => void;
  resetTrigger?: number;
  selectedTemplateId?: string | undefined; // Control tree selection from parent (e.g., after clone)
  expandFolderName?: string | undefined; // Folder to expand after operations (rename/clone/save)
};

// ============================================================================
// Component
// ============================================================================

const TemplateWorkspace = ({
  onEmailTemplateLibraryClick,
  isLeftCollapsed,
  onToggleLeft,
  resetTrigger = 0,
  category = "Email",
  isTemplateDraft: _isTemplateDraft = false,
  isSaveAsDraftDisabled = true,
  onSaveAsDraft: _onSaveAsDraft,
  onTemplateSelected,
  onTemplateLoading: _onTemplateLoading,
  selectedTemplateId,
  expandFolderName,
}: TemplateWorkspaceProps) => {
  // ==========================================================================
  // Redux State
  // ==========================================================================
  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectAccountId);
  const clientId = useAppSelector(selectClientId);
  const userRole = useAppSelector(selectUserRole);
  const isLoading = useAppSelector(selectTemplatesLoading);
  const dataByCategory = useAppSelector(selectDataByCategory);
  const allTemplateFolders = useAppSelector(selectAllTemplateFolders);
  const activeFolderNames = useAppSelector(selectActiveFolderNames);

  // ==========================================================================
  // Local State
  // ==========================================================================
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);
  const [treeKey, setTreeKey] = useState(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]); // Persists across tree refreshes
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null); // Persists across template refetches

  // ==========================================================================
  // Refs
  // ==========================================================================
  const selectedTemplateIdRef = useRef<string | null>(null); // Track currently selected template to prevent re-selection
  const lastAppliedExternalSelectionRef = useRef<string | null>(null); // Avoid re-applying the same parent selection after local folder clicks
  const initialSelectionAppliedRef = useRef<number>(-1); // Track whether initial Drafts selection has been applied
  const prevAccountIdRef = useRef<string | undefined>(undefined); // Track previous accountId to detect changes
  const prevClientIdRef = useRef<string | undefined>(undefined); // Track previous clientId to detect changes

  // ==========================================================================
  // Memoized Values - Template Data
  // ==========================================================================
  // Transform category data into tree data
  // IMPORTANT: We always read from the same arrays regardless of which category was fetched
  // - Global: Always from OPTUM response's globalTemplates
  // - Platform: From PLATFORM (or CLIENT if PLATFORM not fetched) response's platformTemplates
  // - Client: From CLIENT response's clientTemplates
  const globalTemplates = useMemo(
    () => transformFoldersToTreeData(dataByCategory.OPTUM?.globalTemplates),
    [dataByCategory.OPTUM],
  );

  const platformTemplates = useMemo(
    () => transformFoldersToTreeData(
      (dataByCategory.PLATFORM || dataByCategory.CLIENT)?.platformTemplates,
    ),
    [dataByCategory.PLATFORM, dataByCategory.CLIENT],
  );

  const clientTemplates = useMemo(
    () => transformFoldersToTreeData(dataByCategory.CLIENT?.clientTemplates),
    [dataByCategory.CLIENT],
  );

  // Combine templates based on user role and what IDs are selected
  const templatesTreeData = useMemo(() => {
    const hasAccountId = accountId && accountId !== '';
    const hasClientId = clientId && clientId !== '';
    
    if (hasAccountId && hasClientId) {
      return clientTemplates;
    } else if (hasAccountId) {
      return platformTemplates;
    } else {
      return globalTemplates;
    }
  }, [globalTemplates, platformTemplates, clientTemplates, accountId, clientId]);

  const activeTreeData = useMemo(() => {
    return isLoading ? [] : templatesTreeData;
  }, [templatesTreeData, isLoading]);

  // Transform tree data to tree node format for the Tree component
  const treeNode = useMemo(() => {
    const nodes = activeTreeData.map((category) => ({
      id: category.id,
      label: category.label,
      children: category.templates.map((template) => ({
        id: template.id,
        label: template.label,
      })),
    }));

    // If no templates available, show default Drafts folder
    if (nodes.length === 0) {
      return [{ id: "drafts-default", label: "Drafts", children: [] }];
    }

    return nodes;
  }, [activeTreeData]);

  // Some Tree implementations cache internal node state aggressively.
  // Include a lightweight data signature in the key so newly fetched
  // templates force a re-mount and render immediately.
  const treeDataSignature = useMemo(() => {
    return treeNode
      .map((folder) => {
        const childSignature = (folder.children || [])
          .map((child) => `${child.id}:${child.label}`)
          .join(",");
        return `${folder.id}:${folder.label}:${childSignature}`;
      })
      .join("|");
  }, [treeNode]);

  // ==========================================================================
  // Effects - Reset & Initialization
  // ==========================================================================
  // On Home reset: remount the tree AND collapse all folders
  useEffect(() => {
    if (resetTrigger > 0) {
      setTreeKey((prev) => prev + 1);
      setExpandedIds([]);
      selectedTemplateIdRef.current = null;
      lastAppliedExternalSelectionRef.current = null;
      setSelectedTreeId(null);
      dispatch(updateMetadata({ selectedFolder: "" }));
    }
  }, [resetTrigger, dispatch]);

  // Force tree remount when account/client changes - only on actual changes, not re-renders
  useEffect(() => {
    // Skip check on initial mount
    if (prevAccountIdRef.current === undefined && prevClientIdRef.current === undefined) {
      prevAccountIdRef.current = accountId;
      prevClientIdRef.current = clientId;
      return;
    }
    
    const accountIdChanged = prevAccountIdRef.current !== accountId;
    const clientIdChanged = prevClientIdRef.current !== clientId;
    
    if (accountIdChanged || clientIdChanged) {
      setTreeKey((prev) => prev + 1);
      selectedTemplateIdRef.current = null;
      lastAppliedExternalSelectionRef.current = null;
      setSelectedTreeId(null);
      dispatch(updateMetadata({ selectedFolder: "" }));
    }
    
    prevAccountIdRef.current = accountId;
    prevClientIdRef.current = clientId;
  }, [accountId, clientId, dispatch]);

  // ==========================================================================
  // Effects - Parent-Controlled State Sync
  // ==========================================================================
  // Update tree selection when parent provides new selectedTemplateId (e.g., after clone)
  useEffect(() => {
    if (
      selectedTemplateId &&
      selectedTemplateId !== lastAppliedExternalSelectionRef.current
    ) {
      setSelectedTreeId(selectedTemplateId);
      selectedTemplateIdRef.current = selectedTemplateId;
      lastAppliedExternalSelectionRef.current = selectedTemplateId;

      // Keep the parent-selected template visible in the tree by expanding
      // the folder that contains it.
      for (const folder of allTemplateFolders) {
        const templateExists = folder.templates.some(
          (template) => template.templateId === selectedTemplateId,
        );
        if (templateExists) {
          setExpandedIds([folder.folderName]);
          dispatch(updateMetadata({ selectedFolder: folder.folderName }));
          break;
        }
      }
    } else if (selectedTemplateId === "") {
      setSelectedTreeId(null);
      selectedTemplateIdRef.current = null;
      lastAppliedExternalSelectionRef.current = null;
    }
  }, [selectedTemplateId, allTemplateFolders, dispatch]);

  // Expand specified folder when parent requests it (after rename/clone/save)
  useEffect(() => {
    if (expandFolderName) {
      setExpandedIds([expandFolderName]);
    } else if (expandFolderName === "") {
      setExpandedIds([]);
    }
  }, [expandFolderName]);

  // ==========================================================================
  // Effects - Redux Metadata Sync
  // ==========================================================================
  // Store active folder names in Redux metadata for use by modals
  useEffect(() => {
    const folderNames = activeFolderNames.length > 0 ? activeFolderNames : ["Drafts"];
    dispatch(setFolderNames({ folderNames }));
  }, [activeFolderNames, dispatch]);

  // On initial tree load (per treeKey), highlight the Drafts folder as default selection
  useEffect(() => {
    if (isLoading || treeNode.length === 0) return;
    if (initialSelectionAppliedRef.current === treeKey) return;

    // When parent requests a template selection (e.g. Save As Draft with
    // category/scope change), do not override it with Drafts default selection.
    if (selectedTemplateId) {
      initialSelectionAppliedRef.current = treeKey;
      return;
    }

    const draftsNode = treeNode.find((n) => n.label === "Drafts");
    if (!draftsNode) return;

    setSelectedTreeId(null);
    selectedTemplateIdRef.current = null;
    initialSelectionAppliedRef.current = treeKey;
    dispatch(updateMetadata({ selectedFolder: draftsNode.id }));
  }, [treeNode, treeKey, isLoading, dispatch, selectedTemplateId]);

  // ==========================================================================
  // Event Handlers
  // ==========================================================================
  const handleTemplateSelected = (selectedId: string | null) => {
    if (!selectedId) return;

    // Prevent re-selection of the same template
    if (selectedTemplateIdRef.current === selectedId) return;

    const selectedFolderNode = treeNode.find((node) => node.id === selectedId);
    if (selectedFolderNode) {
      if (expandedIds.includes(selectedId)) {
        setExpandedIds([]);
        dispatch(updateMetadata({ selectedFolder: "" }));
      } else {
        setExpandedIds([selectedId]);
        dispatch(updateMetadata({ selectedFolder: selectedId }));
      }
      selectedTemplateIdRef.current = null;
      setSelectedTreeId(null);
      return;
    }

    // From here on, we know it's a child node (template)
    // Search for the template across all category caches
    for (const folder of allTemplateFolders) {
      const template = folder.templates.find((t) => t.templateId === selectedId);
      if (template) {
        setExpandedIds([folder.folderName]);
        dispatch(updateMetadata({ selectedFolder: folder.folderName }));
        selectedTemplateIdRef.current = selectedId;
        setSelectedTreeId(selectedId);

        // Call parent handler to load the template
        if (onTemplateSelected) {
          onTemplateSelected(template, folder.folderName);
        }
        return;
      }
    }
  };

  // Handle folder expansion changes - only allow one folder to be expanded at a time
  const handleExpandedIdsChange = (newExpandedIds: string[]) => {
    if (newExpandedIds.length > 1) {
      const lastExpanded = newExpandedIds[newExpandedIds.length - 1];
      setExpandedIds([lastExpanded]);
      dispatch(updateMetadata({ selectedFolder: lastExpanded }));
    } else {
      setExpandedIds(newExpandedIds);
      dispatch(updateMetadata({ selectedFolder: newExpandedIds[0] || "" }));
    }
  };

  const handleLibraryButtonClick = () => {
    // Show confirmation if Save As Draft button is enabled
    // This means there's work that could potentially be saved
    if (!isSaveAsDraftDisabled) {
      setIsLibraryModalOpen(true);
    } else {
      onEmailTemplateLibraryClick();
    }
  };

  const handleLibraryModalClose = () => {
    setIsLibraryModalOpen(false);
  };

  // Determine if Library button should be disabled based on role and selections
  // TEMPORARILY DISABLED - Debug logs still active
  const isLibraryDisabled = useMemo(() => {
    const disabled = false; // TEMPORARILY ALLOW ALL - remove this line to re-enable restrictions
    // const disabled = (() => {
    //   const hasValidAccountId = accountId && accountId !== "" && accountId !== "0";
    //   const hasValidClientId = clientId && clientId !== "" && clientId !== "0";
    //   if (isPlatformAdmin(userRole)) {
    //     return !hasValidAccountId;
    //   }
    //   if (isClientAdmin(userRole)) {
    //     return !(hasValidAccountId && hasValidClientId);
    //   }
    //   return false;
    // })();
   
    return disabled;
  }, [userRole, accountId, clientId]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div
      className={`col-20 ${isLeftCollapsed ? "is-collapsed" : ""}`}
      aria-expanded={!isLeftCollapsed}
      aria-label="Templates sidebar"
    >
      <div className="demo-content">
        <span
          className={
            isLeftCollapsed ? "collapse-label is-hidden" : "collapse-label"
          }
          aria-hidden={isLeftCollapsed}
        />

        <button
          className="collapse-toggle"
          onClick={onToggleLeft}
          type="button"
          aria-label={
            isLeftCollapsed
              ? "Expand templates panel"
              : "Collapse templates panel"
          }
        >
          {isLeftCollapsed ? (
            <ChevronRightIcon height={30} />
          ) : (
            <ChevronLeftIcon height={30} />
          )}
        </button>
      </div>
      <div className="page">
        <div className="row-1">
          <div className="button-3">
            <Button onPress={handleLibraryButtonClick} size="m" isDisabled={isLibraryDisabled}>
              Template Library
            </Button>
          </div>

          <div className="demo-content-1">
            <Text
              {...textDefaults}
              weight="bold"
              size="xlarge"
              className="tws-panel-title"
            >
              {category} Templates
            </Text>
          </div>

          {/* TODO: Search feature is not yet ready — remove wrapper div once the feature is complete */}
          {/*
          <div className="search-1">
           
            <div className="search-disabled">
              <FormControl id="search-field-basic">
                <SearchField {...searchFieldProps} searchIcon={<SearchIcon />} />
              </FormControl>
            </div>
          </div>
          */}

          <div className="tree-scroll">
            {isLoading ? (
              <div style={{ padding: "20px", textAlign: "center" }}>
                <Text {...textDefaults}>Loading templates...</Text>
              </div>
            ) : treeNode.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "left" }}>
                <Text {...textDefaults} weight="bold">
                  No templates available
                </Text>
              </div>
            ) : (
              <Tree
                key={`${treeKey}-${treeDataSignature}`}
                ariaLabel="Basic tree"
                defaultExpandedIds={expandedIds}
                expandedIds={expandedIds}
                onExpandedIdsChange={handleExpandedIdsChange}
                selectedId={selectedTreeId}
                nodes={treeNode}
                onSelectedIdChange={handleTemplateSelected}
              />
            )}
          </div>
        </div>
      </div>

      <LibraryConfirmationModal
        title="Unsaved Changes"
        open={isLibraryModalOpen}
        onClose={handleLibraryModalClose}
        onContinue={onEmailTemplateLibraryClick}
      />
    </div>
  );
};

export default TemplateWorkspace;
